import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { clearSessionMarkerForSignedOutLogin, getPostLoginPath } from '@/lib/authSession';
import { SignIn, useUser } from '@clerk/clerk-react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Globe,
  Home,
  KeyRound,
  Shield,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

interface BrowserChecks {
  cookiesEnabled: boolean;
  localStorageAvailable: boolean;
  sessionStorageAvailable: boolean;
  secureContext: boolean;
  browserName: string;
}

function runBrowserChecks(): BrowserChecks {
  let localStorageAvailable = false;
  try {
    localStorage.setItem('__es_check', '1');
    localStorage.removeItem('__es_check');
    localStorageAvailable = true;
  } catch {
    /* blocked */
  }

  let sessionStorageAvailable = false;
  try {
    sessionStorage.setItem('__es_check', '1');
    sessionStorage.removeItem('__es_check');
    sessionStorageAvailable = true;
  } catch {
    /* blocked */
  }

  const ua = navigator.userAgent;
  let browserName = 'Unknown Browser';
  if (ua.includes('Edg/')) browserName = 'Microsoft Edge';
  else if (ua.includes('Chrome/')) browserName = 'Google Chrome';
  else if (ua.includes('Firefox/')) browserName = 'Mozilla Firefox';
  else if (ua.includes('Safari/')) browserName = 'Safari';

  return {
    cookiesEnabled: navigator.cookieEnabled,
    localStorageAvailable,
    sessionStorageAvailable,
    secureContext: window.isSecureContext,
    browserName,
  };
}

function formatCountdown(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function CheckRow({ label, passed }: { label: string; passed: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      {passed ? (
        <span className="flex items-center gap-1 text-sm text-green-500">
          <CheckCircle2 className="h-4 w-4" /> OK
        </span>
      ) : (
        <span className="flex items-center gap-1 text-sm text-red-500">
          <XCircle className="h-4 w-4" /> Issue
        </span>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { isSignedIn, isLoaded } = useUser();
  const postLoginPath = getPostLoginPath(search);
  const sessionExpired = new URLSearchParams(search).get('reason') === 'session_expired';

  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [browserChecks, setBrowserChecks] = useState<BrowserChecks | null>(null);

  const originalFetchRef = useRef<typeof window.fetch | null>(null);
  const rateLimitDetectedRef = useRef(false);
  const signInContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clearSessionMarkerForSignedOutLogin(isLoaded, isSignedIn);
  }, [isLoaded, isSignedIn]);

  // Intercept fetch to detect Clerk 429 "Too many requests" responses
  useEffect(() => {
    if (originalFetchRef.current) return;

    const originalFetch = window.fetch;
    originalFetchRef.current = originalFetch;

    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await originalFetch(...args);

      if (response.status === 429) {
        try {
          const url =
            typeof args[0] === 'string'
              ? args[0]
              : args[0] instanceof URL
                ? args[0].href
                : args[0] instanceof Request
                  ? args[0].url
                  : '';

          if (url.includes('.clerk.') && !rateLimitDetectedRef.current) {
            rateLimitDetectedRef.current = true;
            setBrowserChecks(runBrowserChecks());
            setCountdown(15 * 60);
            setShowRateLimitModal(true);
          }
        } catch {
          // Never interfere with the original response
        }
      }

      return response;
    };

    return () => {
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
        originalFetchRef.current = null;
      }
    };
  }, []);

  // Backup: observe Clerk's DOM for rate-limit error text
  useEffect(() => {
    const container = signInContainerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      if (rateLimitDetectedRef.current) return;

      const text = container.textContent?.toLowerCase() || '';
      if (text.includes('too many') || text.includes('rate limit')) {
        rateLimitDetectedRef.current = true;
        setBrowserChecks(runBrowserChecks());
        setCountdown(15 * 60);
        setShowRateLimitModal(true);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  // Countdown timer — each tick sets a new timeout until it hits zero
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleCloseModal = () => {
    setShowRateLimitModal(false);
    rateLimitDetectedRef.current = false;
  };

  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  if (isSignedIn) {
    return <Navigate to={postLoginPath} replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back
          </button>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-4 min-h-[calc(100vh-57px)]">
        {/* Background gradient effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

        <div className="w-full max-w-md animate-fade-in relative">
          {/* Header with Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold">ErgShoot</h1>
            <p className="mt-2 text-muted-foreground">Sign in to your account</p>
            {sessionExpired && (
              <p className="mt-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
                Your session expired. Please sign in again to continue.
              </p>
            )}
          </div>

          {/* Clerk SignIn — wrapped for DOM observation */}
          <div ref={signInContainerRef}>
            <SignIn
              forceRedirectUrl={postLoginPath}
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'bg-card border border-border shadow-lg rounded-lg',
                  headerTitle: 'hidden',
                  headerSubtitle: 'hidden',
                  socialButtonsBlockButton:
                    'bg-secondary hover:bg-secondary/80 text-foreground border-border',
                  formFieldInput:
                    'bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary',
                  formButtonPrimary: 'bg-primary hover:bg-primary/90 text-primary-foreground',
                  footerActionLink: 'text-primary hover:text-primary/80',
                  formFieldAction: 'text-primary hover:text-primary/80 text-base font-medium',
                  identityPreviewEditButton: 'text-primary hover:text-primary/80',
                  formFieldLabel: 'text-foreground',
                  dividerLine: 'bg-border',
                  dividerText: 'text-muted-foreground',
                  footer: 'hidden',
                },
                variables: {
                  colorPrimary: '#FFBB38',
                  colorBackground: '#1a1a1a',
                  colorText: '#FAFAFA',
                  colorInputBackground: '#141414',
                  colorInputText: '#FAFAFA',
                  borderRadius: '0.5rem',
                },
              }}
              signUpUrl="/register"
            />
          </div>

          {/* Forgot Password Help */}
          <div className="mt-6 p-4 bg-card border border-primary/30 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <KeyRound className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-base mb-2">
                  Forgot your password?
                </p>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Enter your email above</li>
                  <li>
                    Click{' '}
                    <span className="text-primary font-medium">&quot;Forgot password?&quot;</span>{' '}
                    on the next step
                  </li>
                  <li>
                    Check your email <span className="text-amber-400">(including spam/junk)</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Register Link */}
          <div className="mt-6 pt-4 border-t border-border text-center text-muted-foreground">
            Don&apos;t have an account?{' '}
            <a
              href="/register"
              className="text-primary hover:text-primary/80 font-semibold transition-colors"
            >
              Create one
            </a>
          </div>
        </div>
      </div>

      {/* Rate Limit Help Modal */}
      <Dialog open={showRateLimitModal} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <Shield className="h-5 w-5" />
              Too Many Sign-In Attempts
            </DialogTitle>
            <DialogDescription>
              For your security, sign-in has been temporarily paused after multiple attempts. This
              is completely normal and will resolve on its own.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Countdown */}
            {countdown > 0 ? (
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-primary/10 border border-primary/20">
                <Clock className="h-6 w-6 text-primary" />
                <p className="text-sm text-muted-foreground">Please wait before trying again</p>
                <p className="text-3xl font-bold font-mono text-primary">
                  {formatCountdown(countdown)}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
                <p className="text-sm font-medium text-green-500">
                  You can try signing in again now
                </p>
              </div>
            )}

            {/* Browser Health Check */}
            {browserChecks && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Browser Health Check</p>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {browserChecks.browserName}
                  </span>
                </div>
                <div className="rounded-lg border border-border p-3 divide-y divide-border">
                  <CheckRow label="Cookies" passed={browserChecks.cookiesEnabled} />
                  <CheckRow label="Local Storage" passed={browserChecks.localStorageAvailable} />
                  <CheckRow
                    label="Session Storage"
                    passed={browserChecks.sessionStorageAvailable}
                  />
                  <CheckRow
                    label="Secure Connection (HTTPS)"
                    passed={browserChecks.secureContext}
                  />
                </div>
                {(!browserChecks.cookiesEnabled || !browserChecks.localStorageAvailable) && (
                  <p className="mt-2 text-xs text-amber-400">
                    <AlertTriangle className="inline h-3 w-3 mr-1" />
                    Some browser features are blocked. Try disabling private/incognito mode or
                    adjusting your browser privacy settings.
                  </p>
                )}
              </div>
            )}

            {/* Next Steps */}
            <div>
              <p className="text-sm font-medium mb-2">
                If it still doesn&apos;t work after 15 minutes:
              </p>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>
                  Use <span className="text-primary font-medium">&quot;Forgot password?&quot;</span>{' '}
                  on the sign-in form to reset your password
                </li>
                <li>Clear your browser cache and cookies, then try again</li>
                <li>
                  Try a different browser{' '}
                  <span className="text-muted-foreground/70">(Chrome, Edge, or Safari)</span>
                </li>
                <li>Contact your ErgShoot administrator for further assistance</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleCloseModal} className="w-full">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
